import { useTheme, Typography, Button } from "@mui/material";
import Box from "@mui/material/Box";
import { StartScreenRoot } from "./StartScreen.styles";
import { BackgroundMedia } from "../BackgroundMedia";
import { LanguageSwitch } from "../LanguageSwitch";
import switchboombangLogo from "../../assets/switchboombang_logo.png";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";

export const StartScreen = ({
  onHandleClose,
  videoInitialized,
  arcwareApplication,
  onSendResOnStart,
}) => {
  const theme = useTheme();
  const streamingSession = localStorage.getItem("streamingSession");

  const { currentTranslations } = useSelectedOptions();

  const clickOnAction = (descriptor) => {
    arcwareApplication?.emitUIInteraction(descriptor);
  };

  const handleStart = () => {
    const orientation =
      window.innerWidth > window.innerHeight ? { zoom: "0" } : { zoom: "-5" };

    clickOnAction(orientation);
    onHandleClose(false, "click");

    // Send the resolution command after 1s
    const timeout = setTimeout(() => {
      onSendResOnStart(true);
    }, 750);

    if (!streamingSession) localStorage.setItem("streamingSession", new Date());

    // Clean up timer if component unmounts early
    return () => clearTimeout(timeout);
  };

  return (
    <StartScreenRoot>
      <LanguageSwitch />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          position: "absolute",
          overflow: "hidden",
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <BackgroundMedia />
      </Box>
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          padding: { xs: "0 3rem", lg: "0 15%" },
          justifyContent: { xs: "flex-end", lg: "center", xl: "center" },
          alignItems: { xs: "flex-start", lg: "flex-end" },
          gap: { xs: "2rem", lg: "3rem" },
          [theme.breakpoints.down("lg")]: {
            "@media (orientation: landscape)": {
              alignItems: "flex-end",
              justifyContent: "center",
              padding: "0 10rem",
            },
          },
        }}
      >
        <Box
          component="img"
          src={switchboombangLogo}
          alt="Fallback"
          sx={{
            width: { xs: "30%", lg: "15%", xl: "15%" },
            height: "auto",
            display: "block",
            zIndex: 1,
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                width: "15rem",
              },
            },
            [theme.breakpoints.between("md", "lg")]: {
              "@media (orientation: landscape)": {
                position: "absolute",
                top: 15,
                left: 15,
              },
            },
          }}
        />
        <Typography
          variant="h4"
          sx={{
            color: theme?.palette?.companyColors?.secondary,
            maxWidth: { xs: "100%", lg: "50%" },
            textAlign: { xs: "left", lg: "right" },
            position: "relative",
            textTransform: "uppercase",
            lineHeight: 1,
            marginTop: { xs: 0, lg: -2.5 },
            fontWeight: 500,
            borderBottom: `1px solid ${theme?.palette?.companyColors?.secondary}`,
            paddingBottom: 1,
          }}
        >
          {currentTranslations?.startScreen?.subtitle}
        </Typography>
        <Typography
          variant="h1"
          sx={{
            maxWidth: { xs: "100%", lg: "45%" },
            textAlign: { xs: "left", lg: "right" },
            position: "relative",
            textTransform: "uppercase",
            lineHeight: 1,
            marginBottom: -1.5,
            fontWeight: 800,
            fontSize: { xs: "4rem", lg: "4.5rem" },
            color: theme?.palette?.companyColors?.accent,
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                maxWidth: "45%",
                textAlign: "right",
              },
            },
            [theme.breakpoints.between("md", "lg")]: {
              "@media (orientation: landscape)": {
                maxWidth: "55%",
              },
            },
          }}
        >
          {currentTranslations?.startScreen?.title}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: { xs: "left", lg: "right" },
            position: "relative",
            fontSize: "1.75rem",
            maxWidth: { xs: "100%", lg: "45%" },
            marginTop: { xs: "15px", lg: "0" },
            color: theme?.palette?.companyColors?.secondary,
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                textAlign: "right",
                maxWidth: "30%",
                marginTop: "0",
              },
            },
            [theme.breakpoints.between("md", "lg")]: {
              "@media (orientation: landscape)": {
                maxWidth: "55%",
              },
            },
          }}
        >
          {currentTranslations?.startScreen?.text}
        </Typography>
        <Button
          sx={{
            color: theme?.palette?.companyColors?.secondary,
            background: theme?.palette?.companyColors?.accent,
            fontWeight: 300,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            textTransform: "initial",
            gap: "0.5rem",
            margin: { xs: "1.5rem 0", lg: "3rem 0" },
            fontSize: { xs: "1.75rem", lg: "2rem" },
            padding: { xs: "1rem 4rem", xl: "1.25rem 7.5rem" },
            transition: "all 0.3s ease",
            pointerEvents: !videoInitialized ? "none" : "all",
            cursor: !videoInitialized ? "default" : "pointer",
            opacity: !videoInitialized ? 0.5 : 1,
            borderRadius: theme?.borderRadius?.small,
            [theme.breakpoints.down("lg")]: {
              "@media (orientation: landscape)": {
                margin: 0,
              },
            },
            "& svg": {
              transition: "all 0.3s ease",
            },
          }}
          onClick={() => {
            if (!videoInitialized) return;
            handleStart();
            localStorage.setItem("streamingSession", new Date());
          }}
        >
          {!videoInitialized ? (
            <>
              {currentTranslations?.startScreen?.buttonLoading}
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
              <Box component="span" className="start-screen__loading__dot">
                .
              </Box>
            </>
          ) : (
            <>
              <Box component="span">
                {streamingSession
                  ? currentTranslations?.startScreen?.buttonResume
                  : currentTranslations?.startScreen?.buttonStart}
              </Box>
            </>
          )}
        </Button>
      </Box>
    </StartScreenRoot>
  );
};
